import { z } from "zod";
import { defineStepConfig, VisibilityPredicateType } from "@erp/workflows";
import { entityOfType } from "../../../utils/system-entities";
import { IntegrationKey, SystemEntity } from "@erp/shared-types";

/**
 * Fetch Sales Order from Famous Step
 *
 * Fetches sales order(s) from Famous Software by order number(s) and ingests them
 * into our database as SalesOrder(s) with line items.
 *
 * This step:
 * - Connects to Famous Oracle database
 * - Retrieves SO data with line items
 * - Validates with FamousSalesOrderSchema
 * - Creates/updates SalesOrder and line items
 * - Handles all edge cases (deleted lines, customer lookup, etc.)
 *
 * Note: Transfer orders (SOTYPE='9') are handled by the separate
 * `fetch_transfer_order_from_famous` step.
 *
 * Input: `orderNumbers` (array of Famous SO numbers) must be provided at runtime.
 *
 * Note: This is a backend-only step that requires Famous database access.
 */
export const fetchSalesOrderFromFamousStep = defineStepConfig({
  type: "fetch_sales_order_from_famous",
  name: "Fetch SO from Famous",
  description: "Fetch and ingest sales order(s) from Famous Software",
  category: "Integration",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({
    orderNumbers: z
      .array(z.string().trim())
      .describe("Array of Famous sales order numbers to fetch"),
  }),

  outputSchema: z.object({
    salesOrders: z
      .array(entityOfType(SystemEntity.AR_SALES_ORDER))
      .describe("Array of Sales Orders (created/updated)")
      .optional(),
  }),

  configSchema: z.object({
    cancelIfNotFound: z
      .boolean()
      .prefault(false)
      .describe("Cancel the workflow if any sales order is not found"),
  }),

  emits: [] as const,
});
